import { IconDefinition } from "@fortawesome/fontawesome-svg-core"
import { faFileChartColumn, faSquareInfo } from "@fortawesome/pro-regular-svg-icons"
import {
  faArrowUpArrowDown,
  faChevronDown,
  faChevronsLeft,
  faChevronsRight,
  faFilterList
} from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Accordion, AccordionDetails, AccordionSummary, Typography } from "@mui/material"
import { Box } from "@mui/system"
import { translate } from "app/language/service"
import { IRootState } from "app/model"
import React, { PropsWithChildren } from "react"
import { connect } from "react-redux"
import {
  IReportForm,
  REPORT_PARAMETERS_MENU_CLOSED_WIDTH_IN_REM,
  REPORT_PARAMETERS_MENU_OPENED_WIDTH_IN_REM
} from "reports/model"
import { getReportForm, getReportsFilterChangeCount, setIsParametersPanelOpened } from "reports/tools/ui"
import { FlexBox } from "ui/components/FlexBox"
import { ReportInfos } from "./ReportInfos"
import { ReportParametersFilters } from "./ReportParametersFilters"
import { ReportParametersGroupBy } from "./ReportParametersGroupBy"
import { ReportParametersSortBy } from "./ReportParametersSortBy"

interface IReportParametersMenuProps {
  isCreation: boolean
}

interface IReportParametersMenuConnectProps {
  reportForm: IReportForm
  isOpened: boolean
}

interface IReportMenuProps {
  id: string
  title: string
  count: number
  icon: IconDefinition
  isOpened: boolean
  isCreation: boolean
}

const ReportMenu = ({
  id,
  title,
  icon,
  children,
  count,
  isOpened,
  isCreation
}: PropsWithChildren<IReportMenuProps>) => {
  if (!isOpened) {
    return (
      <FlexBox
        gap=".3rem"
        sx={{ width: "4rem", height: "4rem", cursor: "pointer" }}
        onClick={() => setIsParametersPanelOpened(isCreation, true)}>
        <FontAwesomeIcon icon={icon} className="icon menu" />
        {count > -1 && <Typography color="textPrimary">({count})</Typography>}
      </FlexBox>
    )
  }
  return (
    <Accordion defaultExpanded sx={{ maxWidth: "100%" }}>
      <AccordionSummary
        expandIcon={<FontAwesomeIcon icon={faChevronDown} className="icon" />}
        aria-controls={`${id}-content`}
        id={`${id}-header`}>
        <FlexBox gap>
          <FontAwesomeIcon icon={icon} />
          <Typography color="textPrimary">
            {title}
            {count > -1 && ` ${count}`}
          </Typography>
        </FlexBox>
      </AccordionSummary>
      <AccordionDetails>
        <FlexBox gap ai="start" jc="start" vertical fullWidth sx={{ "& > *": { width: "100%" } }}>
          {children}
        </FlexBox>
      </AccordionDetails>
    </Accordion>
  )
}

const ReportParametersMenuFn = React.memo(
  ({ isCreation, isOpened, reportForm }: IReportParametersMenuProps & IReportParametersMenuConnectProps) => {
    const { groupBys, sortBys, filters } = reportForm.parameters
    const name = reportForm.report ? reportForm.report.name : ""
    const description = reportForm.report ? reportForm.report.description : ""
    return (
      <FlexBox
        fullHeight
        sx={{
          transition: "all 400ms ease",
          position: "relative",
          backgroundColor: theme => theme.palette.background.paper,
          width: isOpened ? REPORT_PARAMETERS_MENU_OPENED_WIDTH_IN_REM : REPORT_PARAMETERS_MENU_CLOSED_WIDTH_IN_REM,
          minWidth: isOpened ? REPORT_PARAMETERS_MENU_OPENED_WIDTH_IN_REM : REPORT_PARAMETERS_MENU_CLOSED_WIDTH_IN_REM,
          maxWidth: isOpened ? REPORT_PARAMETERS_MENU_OPENED_WIDTH_IN_REM : REPORT_PARAMETERS_MENU_CLOSED_WIDTH_IN_REM
        }}>
        <Box
          id="reports-menu-toggle-button"
          sx={{
            "position": "absolute",
            "top": 0,
            "borderBottomLeftRadius": ".5rem",
            "borderRight": theme => `1px solid ${theme.palette.background.default}`,
            "right": isOpened ? REPORT_PARAMETERS_MENU_OPENED_WIDTH_IN_REM : REPORT_PARAMETERS_MENU_CLOSED_WIDTH_IN_REM,
            "width": "2rem !important",
            "height": "calc(3rem - 1px)",
            "display": "flex",
            "alignItems": "center",
            "justifyContent": "center",
            "cursor": "pointer",
            "backgroundColor": theme => theme.palette.background.paper,
            "transition": "all 400ms ease",
            "& .menu-close": {
              color: theme => theme.palette.text.primary
            }
          }}
          onClick={() => setIsParametersPanelOpened(isCreation, !isOpened)}>
          <FontAwesomeIcon icon={isOpened ? faChevronsRight : faChevronsLeft} size="lg" className="menu-close" />
        </Box>
        <FlexBox
          jc="start"
          vertical
          gap={!isOpened}
          sx={{
            "position": "relative",
            "transition": "all 400ms ease",
            "width": isOpened ? REPORT_PARAMETERS_MENU_OPENED_WIDTH_IN_REM : REPORT_PARAMETERS_MENU_CLOSED_WIDTH_IN_REM,
            "minWidth": isOpened
              ? REPORT_PARAMETERS_MENU_OPENED_WIDTH_IN_REM
              : REPORT_PARAMETERS_MENU_CLOSED_WIDTH_IN_REM,
            "maxWidth": isOpened
              ? REPORT_PARAMETERS_MENU_OPENED_WIDTH_IN_REM
              : REPORT_PARAMETERS_MENU_CLOSED_WIDTH_IN_REM,
            "backgroundColor": theme => theme.palette.background.paper,
            "height": "100%",
            "minHeight": "100%",
            "maxHeight": "100%",
            "overflow": "auto",
            "& > *": {
              width: "100%"
            },
            "& .MuiAccordion-root.Mui-expanded": {
              margin: 0,
              marginTop: "1px"
            },
            "& .icon": {
              height: "1.5rem",
              color: theme => theme.palette.text.primary
            },
            "& .icon.menu": {
              cursor: "pointer"
            },
            "& .inline-edit": {
              ml: "1rem",
              width: "calc(100% - 2rem)"
            }
          }}>
          {!isCreation && (
            <ReportMenu
              id="report-menu-info"
              isOpened={isOpened}
              count={-1}
              title={translate("reports.info.title")}
              icon={faSquareInfo}
              isCreation={isCreation}>
              <ReportInfos />
            </ReportMenu>
          )}
          <ReportMenu
            id="report-menu-group-by"
            isOpened={isOpened}
            count={groupBys.length}
            title={translate("report.axe.title")}
            icon={faFileChartColumn}
            isCreation={isCreation}>
            <ReportParametersGroupBy isCreation={isCreation} />
          </ReportMenu>
          <ReportMenu
            id="report-menu-filters"
            isOpened={isOpened}
            count={getReportsFilterChangeCount(filters)}
            title={translate("label.filters")}
            icon={faFilterList}
            isCreation={isCreation}>
            <ReportParametersFilters isCreation={isCreation} />
          </ReportMenu>
          <ReportMenu
            id="report-menu-sort-by"
            isOpened={isOpened}
            count={sortBys.filter(s => groupBys.includes(s)).length}
            title={translate("label.sort.by")}
            icon={faArrowUpArrowDown}
            isCreation={isCreation}>
            <ReportParametersSortBy isCreation={isCreation} />
          </ReportMenu>
        </FlexBox>
      </FlexBox>
    )
  }
)

export const ReportParametersMenu = connect(
  (state: IRootState, ownProps: IReportParametersMenuProps): IReportParametersMenuConnectProps => {
    const form = getReportForm(ownProps.isCreation, state.reports)
    return {
      reportForm: getReportForm(ownProps.isCreation, state.reports),
      isOpened: form.isParametersPanelOpened
    }
  }
)(ReportParametersMenuFn)
